import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Dialog } from '@mui/material';

import {
    ADDRESS_LINE_1_MAX_LENGTH,
    ADDRESS_LINE_2_MAX_LENGTH,
    ADDRESS_LINE_3_MAX_LENGTH,
    CITY_MAX_LENGTH,
    MAX_EMAIL,
    MAX_NAME,
    MAX_PHONE,
    MAX_POSTCODE
} from '../../../utils/formValidation/commonConstants';
import { uuid } from '../../../utils/stringUtils';
import Button from '../../formElements/Button';
import TextInputField from '../../formElements/TextInputField';

import { validate } from './validateAdditionalContacts';

import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';

const _RELATIONSHIP_MAX_LENGTH = 100;

const EditAdditionalContact = ({
    contact,
    participantId,
    isDisabled = false,
    isOpen,
    onCancel,
    onUpdateContact,
    onAddContact,
    editType
}) => {
    // LOCAL STATE
    const initialState = {
        id: uuid(),
        relationshipToParticipant: '',
        firstName: '',
        lastName: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        city: '',
        postcode: '',
        phoneNumber: '',
        emailAddress: '',
        participantId: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [type, setType] = useState('edit');
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // USEEFFECTS
    useEffect(() => {
        setType(editType);
        if (editType === 'edit') {
            setNewEntry(contact);
            setInitialStateOnEntry(contact);
        } else setNewEntry((prev) => ({ ...prev, participantId }));
    }, [editType, contact]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        key in errors && setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENTHANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onClose = () => {
        setNewEntry(initialState);
        setErrors({});
        onCancel();
    };

    const onEditContact = () => {
        const newErrors = validate(newEntry, type, initialStateOnEntry);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        type === 'create' ? onAddContact(newEntry) : onUpdateContact(newEntry);
        setNewEntry(initialState);
        setInitialStateOnEntry(initialState);
        setErrors({});
    };

    // RENDER
    return (
        <Dialog
            className="muiDialogWrapper"
            open={isOpen}
            onClose={onClose}
            fullWidth
            aria-labelledby="form-dialog-title">
            <div className={modal.modalHeader} id="additional-contact-modal-title">
                <h3>{type === 'create' ? 'Add' : 'Edit'} Contact Details</h3>
            </div>
            <div className={modal.modalContent}>
                <form className={form.formWrapper} data-testid="additional-contact-edit-form">
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <TextInputField
                                id="relationshipToParticipant"
                                label="Relationship to Participant"
                                placeholder="Enter relationship to participant"
                                disabled={isDisabled}
                                mandatory={true}
                                maxLength={_RELATIONSHIP_MAX_LENGTH}
                                value={newEntry.relationshipToParticipant || ''}
                                error={errors.relationshipToParticipant}
                                onChange={(e) =>
                                    onUpdate('relationshipToParticipant', e.target.value)
                                }
                            />
                            <TextInputField
                                id="firstName"
                                label="First Name"
                                placeholder="Enter first name"
                                disabled={isDisabled}
                                mandatory={true}
                                maxLength={MAX_NAME}
                                value={newEntry.firstName || ''}
                                error={errors.firstName}
                                onChange={(e) => onUpdate('firstName', e.target.value)}
                            />
                            <TextInputField
                                id="lastName"
                                label="Last Name"
                                placeholder="Enter last name"
                                disabled={isDisabled}
                                maxLength={MAX_NAME}
                                value={newEntry.lastName || ''}
                                error={errors.lastName}
                                onChange={(e) => onUpdate('lastName', e.target.value)}
                            />
                            <TextInputField
                                id="phoneNumber"
                                label="Phone Number"
                                placeholder="Enter phone number"
                                disabled={isDisabled}
                                maxLength={MAX_PHONE}
                                value={newEntry.phoneNumber || ''}
                                error={errors.phoneNumber}
                                onChange={(e) => onUpdate('phoneNumber', e.target.value)}
                            />
                            <TextInputField
                                id="emailAddress"
                                label="Email"
                                placeholder="Enter email address"
                                disabled={isDisabled}
                                maxLength={MAX_EMAIL}
                                value={newEntry.emailAddress || ''}
                                error={errors.emailAddress}
                                onChange={(e) => onUpdate('emailAddress', e.target.value)}
                            />
                        </div>

                        <div className={form.formColumn}>
                            <TextInputField
                                id="addressLine1"
                                label="Address Line 1"
                                placeholder="Enter address line 1"
                                disabled={isDisabled}
                                maxLength={ADDRESS_LINE_1_MAX_LENGTH}
                                value={newEntry.addressLine1 || ''}
                                error={errors.addressLine1}
                                onChange={(e) => onUpdate('addressLine1', e.target.value)}
                            />
                            <TextInputField
                                id="addressLine2"
                                label="Address Line 2"
                                placeholder="Enter address line 2"
                                disabled={isDisabled}
                                maxLength={ADDRESS_LINE_2_MAX_LENGTH}
                                value={newEntry.addressLine2 || ''}
                                error={errors.addressLine2}
                                onChange={(e) => onUpdate('addressLine2', e.target.value)}
                            />
                            <TextInputField
                                id="addressLine3"
                                label="Address Line 3"
                                placeholder="Enter address line 3"
                                disabled={isDisabled}
                                maxLength={ADDRESS_LINE_3_MAX_LENGTH}
                                value={newEntry.addressLine3 || ''}
                                error={errors.addressLine3}
                                onChange={(e) => onUpdate('addressLine3', e.target.value)}
                            />
                            <TextInputField
                                id="city"
                                label="Town/City"
                                placeholder="Enter town or city"
                                disabled={isDisabled}
                                maxLength={CITY_MAX_LENGTH}
                                value={newEntry.city || ''}
                                error={errors.city}
                                onChange={(e) => onUpdate('city', e.target.value)}
                            />
                            <TextInputField
                                id="postcode"
                                label="Postcode"
                                placeholder="Enter postcode"
                                disabled={isDisabled}
                                maxLength={MAX_POSTCODE}
                                value={newEntry.postcode || ''}
                                error={errors.postcode}
                                onChange={(e) => onUpdate('postcode', e.target.value.toUpperCase())}
                            />
                        </div>
                    </div>

                    <div className={modal.actionButtons}>
                        <Button
                            id="editContactButton"
                            type="button"
                            disabled={isDisabled || isUpdateButtonDisabled}
                            content={type === 'edit' ? 'Update' : 'Add'}
                            error={errors.button}
                            customErrorClass="formActionsError"
                            clearError={() => clearError('button')}
                            onClick={onEditContact}
                        />
                        <Button
                            id="editContactCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onClose}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

EditAdditionalContact.propTypes = {
    contact: PropTypes.object,
    participantId: PropTypes.string,
    isDisabled: PropTypes.bool,
    isOpen: PropTypes.bool,
    onCancel: PropTypes.func,
    onUpdateContact: PropTypes.func,
    onAddContact: PropTypes.func,
    editType: PropTypes.string
};
export default EditAdditionalContact;
